import { useEffect, useRef, type ReactNode } from 'react'
import { t } from '../../i18n/index.js'
import { Button } from './button.js'

interface DialogProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
}

/**
 * Modal built on the native `<dialog>` element.
 *
 * The platform already handles focus trapping, the backdrop, Escape and the
 * inert background — reimplementing that in React is how accessible modals turn
 * into inaccessible ones.
 */
export function Dialog({ open, onClose, title, description, children, footer }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    if (open && !element.open) {
      element.showModal()
    } else if (!open && element.open) {
      element.close()
    }
  }, [open])

  return (
    <dialog
      ref={ref}
      // Escape and the close button take the same path, so state cannot drift
      // out of sync with what is on screen.
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClose={onClose}
      className={
        'bg-surface-raised text-text m-auto w-[min(32rem,calc(100vw-2rem))] rounded-lg ' +
        'p-0 shadow-lg backdrop:bg-black/40'
      }
    >
      {open && (
        <div className="flex flex-col">
          <header className="border-border flex items-start justify-between gap-4 border-b px-5 py-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold">{title}</h2>
              {description && <p className="text-text-muted text-sm">{description}</p>}
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} aria-label={t().common.close}>
              ✕
            </Button>
          </header>

          <div className="px-5 py-4">{children}</div>

          {footer && (
            <footer className="border-border bg-surface-sunken flex justify-end gap-2 border-t px-5 py-3">
              {footer}
            </footer>
          )}
        </div>
      )}
    </dialog>
  )
}
